/**
 * Volume budget — how many slices can this machine safely volume-render in
 * one shot?
 *
 * WebGL doesn't expose available VRAM, so we combine three signals:
 *   1. GPU renderer string (WEBGL_debug_renderer_info) → family tier
 *   2. navigator.deviceMemory → coarse RAM bucket
 *   3. gl.MAX_3D_TEXTURE_SIZE → hard driver cap on the Z dimension
 *
 * Assumptions per slice: 512×512 Int16 = 512 KB. Actual textures upload
 * as R16_SNORM (2 B/voxel) or R16F (2 B/voxel), so the estimate is close.
 *
 *   128 slices ≈  64 MB VRAM   — safe on any WebGL2 GPU
 *   256 slices ≈ 128 MB VRAM   — fine on discrete GPUs and modern iGPUs
 *   512 slices ≈ 256 MB VRAM   — needs a real workstation GPU
 *
 * User can override in Settings; surfaced via `getUserMaxSlices` /
 * `setUserMaxSlices` (localStorage-backed).
 */

export type GpuTier = 'strong' | 'medium' | 'weak' | 'unknown';

export interface VolumeBudget {
  /** The cap actually used to slice a series when building the volume. */
  maxSlices: number;
  tier: GpuTier;
  /** Short human-readable reason — shown in Settings. */
  reason: string;
}

const USER_OVERRIDE_KEY = 'edgeweb-volume-max-slices';

export function setUserMaxSlices(n: number | null) {
  if (n == null || n <= 0) localStorage.removeItem(USER_OVERRIDE_KEY);
  else localStorage.setItem(USER_OVERRIDE_KEY, String(Math.round(n)));
}

export function getUserMaxSlices(): number | null {
  const raw = Number(localStorage.getItem(USER_OVERRIDE_KEY));
  return raw > 0 ? raw : null;
}

let cached: VolumeBudget | null = null;

export function estimateVolumeBudget(): VolumeBudget {
  if (cached) return cached;

  const override = getUserMaxSlices();
  if (override) {
    cached = {
      maxSlices: override,
      tier: classifyTier(detectGpuRenderer(), (navigator as any).deviceMemory),
      reason: `user override (${override})`,
    };
    return cached;
  }

  const mem = (navigator as any).deviceMemory as number | undefined;
  const renderer = detectGpuRenderer();
  const driverMax = detectMax3DTextureSize();
  const tier = classifyTier(renderer, mem);

  // Recommended cap per tier. Kept well under driverMax so the driver
  // has headroom for other textures (opacity/color LUTs, jitter, etc).
  let recommended: number;
  switch (tier) {
    case 'strong':  recommended = 512; break;   // Nvidia/AMD/Apple discrete + 8GB+ RAM
    case 'medium':  recommended = 256; break;   // decent iGPU + 4GB+ RAM
    case 'weak':    recommended = 128; break;   // Intel iGPU or <4GB RAM
    default:        recommended = 192; break;   // unknown → moderate default
  }

  // Never exceed what the driver can allocate for TEXTURE_3D.
  const maxSlices = Math.min(recommended, driverMax);

  cached = {
    maxSlices,
    tier,
    reason: `${renderer} + ${mem ?? '?'}GB RAM → ${tier} (driver max Z ${driverMax})`,
  };
  return cached;
}

/** Clear the cached budget — call after the user changes the override. */
export function invalidateBudget() {
  cached = null;
}

function classifyTier(
  renderer: 'intel' | 'nvidia' | 'apple' | 'amd' | 'unknown',
  mem: number | undefined,
): GpuTier {
  if (renderer === 'nvidia' || renderer === 'amd') {
    return mem != null && mem >= 8 ? 'strong' : 'medium';
  }
  if (renderer === 'apple') {
    // Apple Silicon unified memory — treat as strong on any modern Mac.
    return 'strong';
  }
  if (renderer === 'intel') {
    return mem != null && mem >= 8 ? 'medium' : 'weak';
  }
  if (mem != null && mem >= 8) return 'medium';
  if (mem != null && mem < 4) return 'weak';
  return 'unknown';
}

/** Coarse GPU family classification from the WebGL renderer string. */
function detectGpuRenderer(): 'intel' | 'nvidia' | 'apple' | 'amd' | 'unknown' {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl2') as WebGL2RenderingContext | null)
      ?? (canvas.getContext('webgl') as WebGLRenderingContext | null);
    if (!gl) return 'unknown';
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    if (!info) return 'unknown';
    const s = String(gl.getParameter((info as any).UNMASKED_RENDERER_WEBGL) || '').toLowerCase();
    if (s.includes('nvidia')) return 'nvidia';
    if (s.includes('apple')) return 'apple';
    if (s.includes('amd') || s.includes('radeon')) return 'amd';
    if (s.includes('intel')) return 'intel';
  } catch { /* ignore */ }
  return 'unknown';
}

/** Driver's MAX_3D_TEXTURE_SIZE — hard upper bound on volume Z depth. */
function detectMax3DTextureSize(): number {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') as WebGL2RenderingContext | null;
    if (!gl) return 256;   // WebGL1 fallback — no 3D textures at all
    const v = gl.getParameter(gl.MAX_3D_TEXTURE_SIZE);
    return typeof v === 'number' && v > 0 ? v : 256;
  } catch { return 256; }
}
